/* The modals: move a job, new job, add or edit a customer, edit a job's
 * details, add or edit a person, a board stage, a colour or a shop notice, the
 * two password dialogs,
 * the searchable picker that stands in for every reference-data list, and the
 * one that asks about leaving work unsaved.
 *
 * Every dialog holding a typed form closes through `st.leave` rather than
 * closing itself, so a backdrop caught with the corner of a thumb cannot throw
 * away a half-entered customer. The two that write straight through — move and
 * the picker — close directly, because there is nothing in them waiting on a
 * Save. */
import type { CSSProperties, ReactNode } from 'react';
import {
  COLOUR_BRAND_MAX,
  COLOUR_CODE_MAX,
  COLOUR_NAME_MAX,
  CUSTOMER_TYPES,
  NOTICE_BODY_MAX,
  NOTICE_LEVEL_LABEL,
  NOTICE_LEVELS,
  NOTICE_TITLE_MAX,
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULE,
  ROLES,
  ROLE_LABEL,
} from '@oneshot2pac/shared';
import { PICKERS, SHEENS, TONES } from '@/data/reference';
import { ROLE_NOTE } from '@/data/people';
import { formatDay, today, type IsoDay } from '@/domain/time';
import { duplicateWarning, findDuplicates } from '@/domain/customers';
import { plannedFinish } from '@/domain/schedule';
import type { JobEdit, JobEditField, Leaving } from '@/store/useApp';
import {
  colourWithCode,
  currentJob,
  defaultStage,
  liveColours,
  nextCode,
  stageName,
  toneOf,
  useApp,
} from '@/store/useApp';
import { NO_AUTOFILL } from '@/ui/autofill';
import { DateField, Dialog, PasswordField, Seg } from '@/ui/controls';
import { Swatch } from '@/ui/Swatch';
import type { Item } from '@/types';

export function Dialogs() {
  const moveOpen = useApp((s) => s.moveOpen);
  const newOpen = useApp((s) => s.newOpen);
  const custOpen = useApp((s) => s.custOpen);
  const jobEdit = useApp((s) => s.jobEdit);
  const personOpen = useApp((s) => s.personOpen);
  const stageOpen = useApp((s) => s.stageOpen);
  const colourOpen = useApp((s) => s.colourOpen);
  const noticeOpen = useApp((s) => s.noticeOpen);
  const resetId = useApp((s) => s.resetId);
  const pwOpen = useApp((s) => s.pwOpen);
  const picker = useApp((s) => s.picker);
  const leaving = useApp((s) => s.leaving);

  return (
    <>
      {moveOpen ? <MoveDialog /> : null}
      {newOpen ? <NewJobDialog /> : null}
      {custOpen ? <CustomerDialog /> : null}
      {jobEdit ? <JobDetailsDialog form={jobEdit} /> : null}
      {personOpen ? <PersonDialog /> : null}
      {stageOpen ? <StageDialog /> : null}
      {colourOpen ? <ColourDialog /> : null}
      {noticeOpen ? <NoticeDialog /> : null}
      {resetId ? <ResetPasswordDialog /> : null}
      {pwOpen ? <ChangePasswordDialog /> : null}
      {picker ? <PickerDialog /> : null}
      {/* Last, and on top of whatever it is asking about. */}
      {leaving ? <LeaveDialog leaving={leaving} /> : null}
    </>
  );
}

/** The question every way out of an unsaved form ends up at.
 *
 *  Keep editing is the primary action and the safe one — the shop is one
 *  mis-tap from here, and the tap that gets you out should be the deliberate
 *  one. Discard changes carries the urgent colour and says what it does, so
 *  nobody has to work out which button loses the work. */
function LeaveDialog({ leaving }: { leaving: Leaving }) {
  const stay = useApp((s) => s.stay);
  const discard = useApp((s) => s.discard);

  return (
    <Dialog
      title="Unsaved changes"
      intro={leaving.what}
      width={400}
      zIndex={70}
      onClose={stay}
      actions={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={discard}
            style={{ height: 48, color: 'var(--urgent)', borderColor: 'var(--urgent)' }}
          >
            Discard changes
          </button>
          <button
            type="button"
            className="btn btn-primary blueprint"
            onClick={stay}
            style={{ height: 48, paddingInline: 18 }}
          >
            Keep editing
          </button>
        </>
      }
    >
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
        Keep editing to go back to it, or discard the changes and carry on.
      </p>
    </Dialog>
  );
}

const optionRow = (selected: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minHeight: 48,
  padding: '0 12px',
  borderRadius: 12,
  cursor: 'pointer',
  textAlign: 'left',
  background: selected ? 'var(--accent-tint)' : 'transparent',
  border: `1px solid ${selected ? 'var(--accent)' : 'var(--line-soft)'}`,
});

const pickFieldStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  height: 48,
  padding: '0 12px',
  border: '1px solid var(--line)',
  borderRadius: 10,
  background: 'var(--surface)',
  fontSize: 15,
  cursor: 'pointer',
  textAlign: 'left',
};

function MoveDialog() {
  const st = useApp();
  const job = currentJob(st);
  // Only ever opened from a job, so there is one — but the board can be empty
  // while it loads, and a dialog is not the place to say so.
  if (!job) return null;

  return (
    <Dialog
      title={`Move ${job.code}`}
      intro={`From ${stageName(st, job.stage)}. The move is recorded with your name and the time.`}
      onClose={() => st.set('moveOpen', false)}
      actions={
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => st.set('moveOpen', false)}
          style={{ height: 48 }}
        >
          Cancel
        </button>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          maxHeight: '52vh',
          overflowY: 'auto',
        }}
      >
        {st.stages.map((s) => {
          const here = s.id === job.stage;
          const n = st.jobs.filter((j) => j.stage === s.id).length;
          const tone = s.tone;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => st.move(s.id)}
              style={{
                ...optionRow(false),
                minHeight: 52,
                padding: '0 14px',
                background: here ? 'var(--line-soft)' : 'transparent',
                border: '1px solid var(--line)',
              }}
            >
              <span
                style={{
                  display: 'block',
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  flex: 'none',
                  background: tone || 'transparent',
                  border: '1px solid var(--ink)',
                }}
              />
              <span
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: '0.09em',
                  textTransform: 'uppercase',
                }}
              >
                {s.name}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-data)',
                  fontSize: 11.5,
                  color: 'var(--ink-muted)',
                  marginLeft: 'auto',
                }}
              >
                {here ? 'Current' : `${n} ${n === 1 ? 'job' : 'jobs'}`}
              </span>
            </button>
          );
        })}
      </div>
    </Dialog>
  );
}

/** Everything on a job the office types, in one form.
 *
 *  It grew out of the dates dialog, which could change three of the facts on
 *  the job header and left the rest — the title, the customer's reference, the
 *  site the work goes back to, the day it was promised — to be corrected by
 *  ringing whoever entered them. A job is editable by whoever may edit the job,
 *  which is the assignment gate every other write inside it goes through, so
 *  if the header shows the fact, this offers the field.
 *
 *  Dates are picked, not typed: four dates on one form is four chances to
 *  write a day the shop cannot read back. The one rule between them is the
 *  estimate against the deadline — the same sum the job header calls a job at
 *  risk by, refused here rather than flagged afterwards. */
function JobDetailsDialog({ form }: { form: JobEdit }) {
  const st = useApp();
  const job = currentJob(st);
  // Only ever opened from a job, and the store fills the form from that job.
  if (!job) return null;

  const finish = plannedFinish(form);
  const deadline = form.deadline;
  const clash = finish !== null && deadline !== null && deadline < finish;
  const ready = Boolean(form.title.trim()) && !clash;

  const close = () => st.leave(() => st.set('jobEdit', null));
  /** The fact that was clicked to get here takes the focus. */
  const on = (field: JobEditField) => st.jobEditFocus === field;
  const setDate =
    (key: 'received' | 'promised' | 'estStart' | 'deadline') => (day: IsoDay | null) =>
      st.setJobEdit({ [key]: day });

  return (
    <Dialog
      title={`Edit ${job.code}`}
      intro="Promised is what the customer was told. The deadline is the date it must be out by — they are not always the same."
      width={640}
      onClose={close}
      actions={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={close}
            style={{ height: 48 }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void st.saveJob()}
            disabled={!ready}
            aria-disabled={!ready}
            style={{
              height: 48,
              ...(ready ? null : { opacity: 0.5, cursor: 'not-allowed' }),
            }}
          >
            Save changes
          </button>
        </>
      }
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px 16px',
          maxHeight: '56vh',
          overflowY: 'auto',
          padding: 2,
        }}
      >
        <Field label="Job title" span>
          <input
            className="input"
            name="title"
            value={form.title}
            autoComplete={NO_AUTOFILL}
            autoFocus={on('title')}
            placeholder="e.g. Ashgrove kitchen"
            onChange={(e) => st.setJobEdit({ title: e.target.value })}
            style={{ height: 48, fontSize: 16 }}
          />
        </Field>

        <Field label="Their reference">
          <input
            className="input"
            name="ref"
            value={form.ref}
            autoComplete={NO_AUTOFILL}
            autoFocus={on('ref')}
            placeholder="The number on their door schedule"
            onChange={(e) => st.setJobEdit({ ref: e.target.value })}
            style={{ height: 48, fontSize: 16 }}
          />
        </Field>

        <Field label="Site">
          <input
            className="input"
            name="site"
            value={form.site}
            autoComplete={NO_AUTOFILL}
            autoFocus={on('site')}
            placeholder="Where it goes back to"
            onChange={(e) => st.setJobEdit({ site: e.target.value })}
            style={{ height: 48, fontSize: 16 }}
          />
          <span style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
            A new address here is added to {job.customer}.
          </span>
        </Field>

        <Field label="Date in">
          <DateField
            name="received"
            value={form.received}
            onChange={setDate('received')}
            autoFocus={on('received')}
            hint="The day the components arrived"
          />
        </Field>

        <Field label="Promised">
          <DateField
            name="promised"
            value={form.promised}
            onChange={setDate('promised')}
            autoFocus={on('promised')}
            hint="What the customer was told"
          />
        </Field>

        <Field label="Est. start">
          <DateField
            name="estStart"
            value={form.estStart}
            onChange={setDate('estStart')}
            autoFocus={on('estStart')}
            hint="When it hits the bench"
          />
        </Field>

        <Field label="Est. days">
          <input
            className="input"
            name="estDays"
            value={`${form.estDays}`}
            autoComplete={NO_AUTOFILL}
            inputMode="numeric"
            autoFocus={on('estDays')}
            onChange={(e) =>
              st.setJobEdit({
                estDays: parseInt(e.target.value.replace(/[^0-9]/g, ''), 10) || 0,
              })
            }
            style={{ height: 48, fontSize: 16 }}
          />
          <span style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
            {finish === null
              ? 'Working days in the shop'
              : `Working days in the shop — finishing ${formatDay(finish, { year: true })}`}
          </span>
        </Field>

        <Field label="Deadline">
          <DateField
            name="deadline"
            value={deadline}
            onChange={setDate('deadline')}
            min={finish}
            autoFocus={on('deadline')}
            hint="The date it must be out by"
            invalid={clash}
            describedBy={clash ? 'dates-clash' : undefined}
          />
        </Field>

        {/* Errors say what happened and how to fix it. The three ways out are
            the three fields, so the sentence names all of them. */}
        {clash && finish !== null ? (
          <p
            id="dates-clash"
            role="alert"
            style={{
              gridColumn: '1 / -1',
              margin: 0,
              fontSize: 12.5,
              color: 'var(--urgent)',
              lineHeight: 1.45,
              border: '1px solid var(--urgent)',
              borderRadius: 12,
              padding: 10,
            }}
          >
            The deadline is before the work is done. {form.estDays || 1} working{' '}
            {(form.estDays || 1) === 1 ? 'day' : 'days'} from {formatDay(form.estStart)} finishes{' '}
            {formatDay(finish, { year: true })}. Move the deadline out, start earlier, or shorten
            the estimate.
          </p>
        ) : null}
      </div>
      <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-muted)' }}>
        A date left empty reads as “Not set” on the job — which is a real answer for a job the
        builder has not confirmed yet.
      </p>
    </Dialog>
  );
}

/** A labelled control on a form: the label above it, and whatever the control
 *  puts under itself below. `span` gives it the whole row — a job title is not
 *  a half-width field. */
function Field({ label, span, children }: { label: string; span?: boolean; children: ReactNode }) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        minWidth: 0,
        gridColumn: span ? '1 / -1' : undefined,
      }}
    >
      <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function NewJobDialog() {
  const st = useApp();
  const d = st.draft;

  // Fields with a fixed value set cycle rather than open a second modal —
  // there are four options at most and a nested picker is worse.
  const fields = [
    { label: 'Customer', key: 'customer', kind: 'pick', opts: st.custs.map((c) => c.name) },
    { label: 'Job title', key: 'title', kind: 'text', hint: 'e.g. Ashgrove kitchen' },
    {
      label: 'Their reference',
      key: 'ref',
      kind: 'text',
      hint: 'The number on their door schedule',
    },
    { label: 'Site', key: 'site', kind: 'text', hint: 'Where it goes back to' },
    { label: 'Colour', key: 'colour', kind: 'pick', opts: liveColours(st).map((c) => c.code) },
    { label: 'Finish', key: 'sheen', kind: 'pick', opts: SHEENS, index: true },
    {
      label: 'Profile',
      key: 'profile',
      kind: 'pick',
      opts: ['Square', 'Shaker', 'V-groove', 'Pencil round'],
    },
    { label: 'Items', key: 'items', kind: 'num', hint: 'Components counted on goods in' },
    { label: 'Date in', key: 'received', kind: 'text', hint: 'e.g. Thu 3 Sep' },
    { label: 'Est. start', key: 'estStart', kind: 'text', hint: 'When it hits the bench' },
    { label: 'Est. days', key: 'estDays', kind: 'num', hint: 'Working days in the shop' },
    { label: 'Date out (deadline)', key: 'deadline', kind: 'text', hint: 'Must be out by' },
  ] as const;

  const cycle = (key: string, opts: readonly string[], value: string, indexed?: boolean) => {
    const next = opts[(opts.indexOf(value) + 1) % opts.length];
    const patch: Record<string, unknown> = { [key]: indexed ? opts.indexOf(next) : next };
    // Changing the customer re-points the site and their reference prefix.
    if (key === 'customer') {
      const c = st.custs.find((x) => x.name === next);
      patch.site = c?.sites[0] ?? '';
      patch.ref = c?.ref ? `${c.ref}-` : '';
    }
    st.setDraft(patch);
  };

  return (
    <Dialog
      title={`New job ${nextCode(st.jobs)}`}
      intro={`It starts at ${defaultStage(st)?.name ?? 'the first stage'}. Tap a field with a value to cycle through the reference data.`}
      width={680}
      onClose={() => st.leave(() => st.set('newOpen', false))}
      actions={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => st.leave(() => st.set('newOpen', false))}
            style={{ height: 48 }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={st.createJob}
            style={{ height: 48 }}
          >
            Create job
          </button>
        </>
      }
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px 16px',
          maxHeight: '56vh',
          overflowY: 'auto',
          padding: 2,
        }}
      >
        {fields.map((f) => {
          const raw = d[f.key as keyof typeof d];
          const value = 'index' in f && f.index ? SHEENS[raw as number] : `${raw}`;
          return (
            <label
              key={f.key}
              style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}
            >
              <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
                {f.label}
              </span>
              {f.kind === 'pick' ? (
                <button
                  type="button"
                  name={f.key}
                  onClick={() => cycle(f.key, f.opts, value, 'index' in f && f.index)}
                  style={pickFieldStyle}
                >
                  <span
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {value}
                  </span>
                  <span style={{ color: 'var(--ink-muted)', fontSize: 12 }}>change</span>
                </button>
              ) : (
                <input
                  className="input"
                  name={f.key}
                  value={value}
                  placeholder={'hint' in f ? f.hint : undefined}
                  autoComplete={NO_AUTOFILL}
                  inputMode={f.kind === 'num' ? 'numeric' : undefined}
                  onChange={(e) =>
                    st.setDraft({
                      [f.key]:
                        f.kind === 'num'
                          ? parseInt(e.target.value.replace(/[^0-9]/g, ''), 10) || 0
                          : e.target.value,
                    })
                  }
                  style={{ height: 48, fontSize: 16 }}
                />
              )}
            </label>
          );
        })}
      </div>
      <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-muted)' }}>
        Dates read like {formatDay(today())}. Leave one empty if it isn’t known yet.
      </p>
    </Dialog>
  );
}

/** Add a customer, or change one already on the list.
 *
 *  One dialog for both, like the person form: the fields are the same, and a
 *  second screen that edited them would be a second place for the duplicate
 *  rule to be got wrong.
 *
 *  That rule is why this form is more than a set of inputs. The office types in
 *  a customer the shop already has — the same builder a year later, spelled
 *  differently — and finds out when their jobs are split across two records. So
 *  the name, email and phone are checked against the customers already loaded
 *  as they type, and the warning names the customer and offers to open them.
 *  The server refuses the write regardless; this is the same answer, sooner. */
function CustomerDialog() {
  const st = useApp();
  const d = st.custDraft;
  const editing = st.custEditId;
  const close = () => st.leave(() => st.set('custOpen', false));
  const ready = Boolean(d.name.trim());
  const editingName = st.custs.find((c) => c.id === editing)?.name;

  const clashes = findDuplicates(
    st.custs,
    { name: d.name, email: d.email, phone: d.phone },
    editing,
  );

  const fields = [
    { label: 'Business or person', key: 'name', hint: 'e.g. Kedron Cabinets' },
    { label: 'Contact', key: 'contact', hint: 'Who you ring' },
    { label: 'Phone', key: 'phone', hint: '04xx xxx xxx' },
    { label: 'Email', key: 'email', hint: 'For the job confirmation' },
    { label: 'Their reference prefix', key: 'ref', hint: 'e.g. KC — leave blank for retail' },
  ] as const;

  return (
    <Dialog
      title={editing ? `Edit ${editingName ?? 'customer'}` : 'Add customer'}
      intro={
        editing
          ? 'Their details show on every job for them. Clearing a field clears it on the customer.'
          : 'Only the name is required — the rest can be filled in later from their page.'
      }
      width={600}
      onClose={close}
      actions={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={close}
            style={{ height: 48 }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready || st.savingCust}
            onClick={() => void st.saveCustomer()}
            style={{ height: 48, opacity: ready && !st.savingCust ? 1 : 0.55 }}
          >
            {st.savingCust ? 'Saving…' : editing ? 'Save changes' : 'Add customer'}
          </button>
        </>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          maxHeight: '58vh',
          overflowY: 'auto',
          padding: 2,
        }}
      >
        {st.custError ? <DialogError>{st.custError}</DialogError> : null}

        {clashes.map((clash) => (
          <div
            key={clash.customer.id}
            role="status"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 12px',
              borderRadius: 10,
              background: 'var(--urgent-tint)',
              border: '1px solid var(--urgent)',
            }}
          >
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>
                {duplicateWarning(clash)}
              </span>
              <span
                style={{
                  display: 'block',
                  fontSize: 12.5,
                  color: 'var(--ink-muted)',
                  lineHeight: 1.45,
                }}
              >
                Add the work to them rather than a second record — two records for one customer
                splits their jobs in half.
              </span>
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                st.set('custOpen', false);
                st.openCustomer(clash.customer.name);
              }}
              style={{ height: 44, marginLeft: 'auto', whiteSpace: 'nowrap' }}
            >
              Open {clash.customer.name}
            </button>
          </div>
        ))}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          {fields.map((f) => (
            <label
              key={f.key}
              style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}
            >
              <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
                {f.label}
              </span>
              <input
                className="input"
                name={f.key}
                value={d[f.key]}
                placeholder={f.hint}
                autoComplete={NO_AUTOFILL}
                onChange={(e) => st.setCustDraft({ [f.key]: e.target.value })}
                style={{ height: 48, fontSize: 16 }}
              />
            </label>
          ))}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
            <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
              Type
            </span>
            <Seg
              height={48}
              fill
              options={CUSTOMER_TYPES.map((t) => ({
                label: t,
                on: d.type === t,
                pick: () => st.setCustDraft({ type: t }),
              }))}
            />
          </div>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
            Sites, one per line
          </span>
          <textarea
            className="input"
            name="sites"
            value={d.sites}
            autoComplete={NO_AUTOFILL}
            placeholder="Where finished work goes back to"
            onChange={(e) => st.setCustDraft({ sites: e.target.value })}
            style={{ width: '100%', minHeight: 76, fontSize: 16, resize: 'vertical' }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
            Notes
          </span>
          <textarea
            className="input"
            name="note"
            value={d.note}
            autoComplete={NO_AUTOFILL}
            placeholder="How they like to work — delivery, handles, invoicing, anything the floor should know"
            onChange={(e) => st.setCustDraft({ note: e.target.value })}
            style={{ width: '100%', minHeight: 76, fontSize: 16, resize: 'vertical' }}
          />
        </label>
      </div>
    </Dialog>
  );
}

/** Add someone to the shop by hand, or change someone already on the list.
 *
 *  Adding creates the account: the office types the password and hands it over,
 *  because there is no mailer yet and no link to send. It stays readable as they
 *  type rather than sitting behind dots — they have to read it back to the
 *  person standing there — and the eye hides it again. Editing never touches a
 *  password; that is theirs.
 *
 *  Neither is a signup. The routes behind this are owner-and-admin only, and
 *  the server is what enforces that (IDEA.md §9). */
function PersonDialog() {
  const st = useApp();
  const d = st.personDraft;
  const editing = st.personEditId;
  const person = editing ? st.people.find((p) => p.id === editing) : undefined;
  const close = () => st.leave(() => st.set('personOpen', false));
  const subbie = d.role === 'subcontractor';
  const ready = editing
    ? Boolean(d.name.trim())
    : Boolean(d.name.trim() && d.email.trim() && d.password);

  /* What the server will refuse, said before anyone tries it: nobody changes
     their own standing, and only an owner touches an owner. The real membership
     decides — the account menu's role preview changes what this build draws,
     never what the server allows. */
  const realRole = st.session?.membership.role;
  const locked = !person
    ? ''
    : person.user.id === st.session?.user.id
      ? 'You cannot change your own role or money access, or turn your own account off. Ask another owner or admin.'
      : person.role === 'owner' && realRole !== 'owner'
        ? 'Only the owner can change an owner.'
        : '';

  const fields = editing
    ? ([
        { label: 'Name', key: 'name', hint: 'As the board should show them', type: 'text' },
      ] as const)
    : ([
        { label: 'Name', key: 'name', hint: 'As the board should show them', type: 'text' },
        { label: 'Email', key: 'email', hint: 'What they sign in with', type: 'email' },
        { label: 'Password', key: 'password', hint: PASSWORD_RULE, type: 'text' },
      ] as const);

  return (
    <Dialog
      title={person ? `Edit ${person.user.name}` : 'Add person'}
      intro={
        person
          ? 'Their email and password stay as they are — this changes what they may do in the shop.'
          : 'They can sign in straight away. Nothing is emailed — write the password down and hand it over.'
      }
      width={560}
      onClose={close}
      actions={
        <>
          {/* Their password is a different job from their standing in the shop,
              so it sits away from Save, on the other side of the row. It hands
              the form over rather than opening a second dialog on top of this
              one — two open at once and the unsaved question would name the
              wrong form. */}
          {person && !locked ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() =>
                st.leave(() => {
                  st.set('personOpen', false);
                  st.openResetPassword(person);
                })
              }
              style={{ height: 48, marginRight: 'auto' }}
            >
              Reset password
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={close}
            style={{ height: 48 }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready || st.savingPerson}
            onClick={() => void st.savePerson()}
            style={{ height: 48, opacity: ready && !st.savingPerson ? 1 : 0.55 }}
          >
            {st.savingPerson ? 'Saving…' : person ? 'Save changes' : 'Add person'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 2 }}>
        {st.personError ? <DialogError>{st.personError}</DialogError> : null}

        {fields.map((f) =>
          /* The password they will be handed is typed on screen, not behind
             dots, and the eye is what puts it back — see PasswordField. */
          f.key === 'password' ? (
            <PasswordField
              key={f.key}
              label={f.label}
              name={f.key}
              value={d[f.key]}
              hint={f.hint}
              shown
              onChange={(v) => st.setPersonDraft({ password: v })}
            />
          ) : (
            <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
                {f.label}
              </span>
              <input
                className="input"
                name={f.key}
                type={f.type}
                value={d[f.key]}
                placeholder={f.hint}
                autoComplete={NO_AUTOFILL}
                onChange={(e) => st.setPersonDraft({ [f.key]: e.target.value })}
                style={{ height: 48, fontSize: 16 }}
              />
            </label>
          ),
        )}

        {person ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
              Email
            </span>
            <span style={{ fontFamily: 'var(--font-data)', fontSize: 13.5 }}>
              {person.user.email}
            </span>
          </div>
        ) : null}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
            Role
          </span>
          {locked ? (
            <span style={{ fontSize: 15, fontWeight: 600 }}>{ROLE_LABEL[d.role]}</span>
          ) : (
            <Seg
              height={44}
              options={ROLES.map((r) => ({
                label: ROLE_LABEL[r],
                on: d.role === r,
                pick: () => st.setPersonDraft({ role: r }),
              }))}
            />
          )}
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-muted)', lineHeight: 1.45 }}>
            {locked || ROLE_NOTE[d.role]}
          </p>
        </div>

        <button
          type="button"
          aria-pressed={d.financialAccess}
          disabled={subbie || Boolean(locked)}
          onClick={() => st.setPersonDraft({ financialAccess: !d.financialAccess })}
          style={{
            ...optionRow(d.financialAccess),
            minHeight: 56,
            cursor: subbie || locked ? 'not-allowed' : 'pointer',
            opacity: subbie || locked ? 0.6 : 1,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 20,
              height: 20,
              flex: 'none',
              borderRadius: 6,
              display: 'grid',
              placeItems: 'center',
              fontSize: 13,
              color: 'var(--on-accent)',
              background: d.financialAccess ? 'var(--accent)' : 'transparent',
              border: `1px solid ${d.financialAccess ? 'var(--accent)' : 'var(--line)'}`,
            }}
          >
            {d.financialAccess ? '✓' : ''}
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Money access</span>
            <span style={{ fontSize: 12.5, color: 'var(--ink-muted)' }}>
              {subbie
                ? 'Subcontractors never see costs, rates, quotes or invoices.'
                : 'Costs, rates, quotes and invoices. Separate from the role.'}
            </span>
          </span>
        </button>

        {person && !locked ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              paddingTop: 12,
              borderTop: '1px solid var(--line-soft)',
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {person.status === 'active' ? 'Active' : 'Deactivated'}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-muted)', lineHeight: 1.45 }}>
                {person.status === 'active'
                  ? 'They can sign in and work on the jobs they are on.'
                  : 'They cannot sign in. Everything they did stays on the jobs.'}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={st.savingPerson}
              onClick={() =>
                void st.setPersonStatus(person.status === 'active' ? 'deactivated' : 'active')
              }
              style={{
                height: 44,
                marginLeft: 'auto',
                whiteSpace: 'nowrap',
                color: person.status === 'active' ? 'var(--urgent)' : undefined,
              }}
            >
              {person.status === 'active' ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Set somebody else's password, because there is no mailer and no reset link.
 *
 *  The office types one and reads it out. It stays visible as they type — the
 *  whole point is to say it to the person standing there — and the eye puts it
 *  back behind dots if somebody is looking over the shoulder. It is never
 *  echoed back afterwards, so it has to be handed over now. */
function ResetPasswordDialog() {
  const st = useApp();
  const person = st.people.find((p) => p.id === st.resetId);
  const close = () => st.leave(() => st.set('resetId', null));
  const ready = st.resetDraft.length >= PASSWORD_MIN_LENGTH;

  return (
    <Dialog
      title={`Set a password for ${person?.user.name ?? 'them'}`}
      intro="Type one and tell them what it is. Nothing is emailed, and it ends every session they have — this password is the way back in."
      onClose={close}
      actions={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={close}
            style={{ height: 48 }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready || st.savingReset}
            onClick={() => void st.resetPassword()}
            style={{ height: 48, opacity: ready && !st.savingReset ? 1 : 0.55 }}
          >
            {st.savingReset ? 'Setting…' : 'Set password'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 2 }}>
        {st.resetError ? <DialogError>{st.resetError}</DialogError> : null}

        <PasswordField
          label="New password"
          name="newPassword"
          value={st.resetDraft}
          hint={PASSWORD_RULE}
          shown
          onChange={(v) => st.setResetPassword(v)}
        />

        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-muted)', lineHeight: 1.45 }}>
          Give them a password to get back in with. They change it to something private from Change
          password in their own account menu.
        </p>
      </div>
    </Dialog>
  );
}

/** Change your own password. Asks for the one you already have, which is what
 *  stops an account left signed in on a bench iPad from being taken over. */
function ChangePasswordDialog() {
  const st = useApp();
  const close = () => st.leave(() => st.set('pwOpen', false));
  const ready = Boolean(st.pwCurrent) && st.pwNext.length >= PASSWORD_MIN_LENGTH;

  const fields = [
    {
      label: 'Current password',
      value: st.pwCurrent,
      name: 'currentPassword',
      hint: 'The one you signed in with',
      change: (v: string) => st.setPassword({ current: v }),
    },
    {
      label: 'New password',
      value: st.pwNext,
      name: 'newPassword',
      hint: PASSWORD_RULE,
      change: (v: string) => st.setPassword({ next: v }),
    },
  ];

  return (
    <Dialog
      title="Change password"
      intro="This signs you out everywhere, on this device too. Sign back in with the new one."
      onClose={close}
      actions={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={close}
            style={{ height: 48 }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready || st.savingPw}
            onClick={() => void st.changePassword()}
            style={{ height: 48, opacity: ready && !st.savingPw ? 1 : 0.55 }}
          >
            {st.savingPw ? 'Changing…' : 'Change password'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 2 }}>
        {st.pwError ? <DialogError>{st.pwError}</DialogError> : null}

        {fields.map((f) => (
          <PasswordField
            key={f.name}
            label={f.label}
            name={f.name}
            value={f.value}
            hint={f.hint}
            onChange={f.change}
          />
        ))}
      </div>
    </Dialog>
  );
}

/** A tick-box row: a checkbox that reads as a sentence, not a bare label. The
 *  money toggle on the person form is the same shape — a flag whose meaning
 *  needs a line of explanation next to it, not a tooltip. */
function CheckOption({
  on,
  label,
  note,
  onToggle,
}: {
  on: boolean;
  label: string;
  note: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onToggle}
      style={{ ...optionRow(on), minHeight: 56, paddingBlock: 10 }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 20,
          height: 20,
          flex: 'none',
          borderRadius: 6,
          display: 'grid',
          placeItems: 'center',
          fontSize: 13,
          color: 'var(--on-accent)',
          background: on ? 'var(--accent)' : 'transparent',
          border: `1px solid ${on ? 'var(--accent)' : 'var(--line)'}`,
        }}
      >
        {on ? '✓' : ''}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left' }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 12.5, color: 'var(--ink-muted)', lineHeight: 1.4 }}>{note}</span>
      </span>
    </button>
  );
}

/** Add a board stage, or change one.
 *
 *  This is the one settings form that changes what the whole shop sees: the
 *  board columns, the move sheet, every stage label on every list. So it says
 *  what each flag does in the shop's own terms, and the rules that keep a board
 *  coherent are enforced in `saveStage` — the office is told which one it hit,
 *  rather than finding out on the board afterwards.
 *
 *  It is laid out to fit an iPad in landscape without scrolling, which is
 *  where the office edits the board: wide rather than tall, with the name and
 *  the WIP limit on one line, the colours in an even grid of two rows, and the
 *  two flags side by side. Each of those is an `auto-fit` grid, so a narrower
 *  screen stacks them again on its own rather than at a picked width. Archive
 *  sits with the actions, the way Take down does on a notice — why a stage
 *  cannot be archived is the refusal's to say, when somebody asks. */
function StageDialog() {
  const st = useApp();
  const closeStage = () => st.leave(st.closeStage);
  const d = st.stageDraft;
  const stage = st.stages.find((x) => x.id === st.stageEditId);
  const ready = d.name.trim().length > 0 && !st.savingStage;
  const label = { letterSpacing: '0.12em' } as const;
  const hint: CSSProperties = {
    margin: 0,
    fontSize: 12.5,
    color: 'var(--ink-muted)',
    lineHeight: 1.45,
  };

  return (
    <Dialog
      title={stage ? `Edit ${stage.name}` : 'Add stage'}
      intro={
        stage
          ? 'The board, the move sheet and every job label read this stage from here.'
          : 'A new stage goes on the end of the board. Move it into place from the list afterwards.'
      }
      width={680}
      onClose={closeStage}
      actions={
        <>
          {stage ? (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={st.savingStage}
              onClick={() => void st.archiveStage()}
              style={{ height: 48, marginRight: 'auto', color: 'var(--urgent)' }}
            >
              Archive
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeStage}
            style={{ height: 48 }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready}
            aria-disabled={!ready}
            onClick={() => void st.saveStage()}
            style={{ height: 48, opacity: ready ? 1 : 0.55 }}
          >
            {st.savingStage ? 'Saving…' : stage ? 'Save changes' : 'Add stage'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 2 }}>
        {st.stageError ? <DialogError>{st.stageError}</DialogError> : null}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(112px, 160px)',
            gap: '6px 12px',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span className="label-xs" style={label}>
              Name
            </span>
            <input
              className="input"
              name="stageName"
              value={d.name}
              placeholder="What the shop calls this step"
              autoComplete={NO_AUTOFILL}
              onChange={(e) => st.setStageDraft({ name: e.target.value })}
              style={{ height: 48, fontSize: 16 }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span className="label-xs" style={label}>
              WIP limit
            </span>
            <input
              className="input"
              name="wip"
              inputMode="numeric"
              value={d.wip}
              placeholder="No limit"
              autoComplete={NO_AUTOFILL}
              onChange={(e) => st.setStageDraft({ wip: e.target.value })}
              style={{ height: 48, fontSize: 16, fontFamily: 'var(--font-data)' }}
            />
          </label>
          <p style={{ ...hint, gridColumn: '1 / -1' }}>
            The WIP limit is how many jobs this step holds at once — the board flags the column when
            it goes over. Blank is no limit.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="label-xs" style={label}>
            Board colour
          </span>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))',
              gap: 6,
            }}
          >
            {TONES.map((t) => {
              const on = d.tone === t.hex;
              return (
                <button
                  key={t.name}
                  type="button"
                  aria-pressed={on}
                  onClick={() => st.setStageDraft({ tone: t.hex })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minHeight: 48,
                    padding: '0 12px',
                    borderRadius: 12,
                    cursor: 'pointer',
                    textAlign: 'left',
                    background: on ? 'var(--accent-tint)' : 'transparent',
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--line-soft)'}`,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'block',
                      width: 18,
                      height: 18,
                      flex: 'none',
                      borderRadius: 6,
                      background: t.hex || 'transparent',
                      border: t.hex ? '1px solid var(--chip-border)' : '1px dashed var(--line)',
                    }}
                  />
                  <span style={{ fontSize: 13.5 }}>{t.name}</span>
                </button>
              );
            })}
          </div>
          <p style={hint}>
            Tints the column. The stage name is always drawn as well, so the colour is never
            carrying it on its own.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 8,
          }}
        >
          <CheckOption
            on={d.def}
            label="New jobs land here"
            note="Where a job starts when the office creates it. Only one stage is the default."
            onToggle={() => st.setStageDraft({ def: !d.def })}
          />

          <CheckOption
            on={d.terminal}
            label="Work has left the shop"
            note="Jobs here drop out of the in-shop counts and stop reading as overdue — Delivered, not Ready."
            onToggle={() => st.setStageDraft({ terminal: !d.terminal })}
          />
        </div>
      </div>
    </Dialog>
  );
}

/** Add a colour to the shop's list, or correct one.
 *
 *  Four fields, because that is what a colour is to this shop: the brand and
 *  the code are what gets re-ordered, the name is what the floor calls it, and
 *  the swatch is the chip on every card. The swatch is a native colour picker
 *  beside a hex field — the picker for matching a chip by eye, the field for
 *  typing the value off a fan deck.
 *
 *  One code is one colour, so the code is checked against the list as it is
 *  typed, the way the customer form checks a phone number: the office is told
 *  before the request goes out. An archived code is not a clash when adding —
 *  it brings that colour back — and the form says so rather than surprising
 *  anyone. */
function ColourDialog() {
  const st = useApp();
  const close = () => st.leave(st.closeColour);
  const d = st.colourDraft;
  const colour = st.colours.find((c) => c.id === st.colourEditId);
  const clash = colourWithCode(st, d.code, st.colourEditId);
  const refused = Boolean(clash && (!clash.archived || colour));
  const hexOk = /^#[0-9a-fA-F]{6}$/.test(d.hex.trim());
  const ready =
    Boolean(d.brand.trim() && d.code.trim() && d.name.trim()) &&
    hexOk &&
    !refused &&
    !st.savingColour;
  const recoded = Boolean(colour && colour.jobs > 0 && d.code.trim() !== colour.code);
  const brands = [...new Set(st.colours.map((c) => c.brand))].sort();
  const label = { letterSpacing: '0.12em' } as const;
  const hint: CSSProperties = {
    margin: 0,
    fontSize: 12.5,
    color: 'var(--ink-muted)',
    lineHeight: 1.45,
  };

  return (
    <Dialog
      title={colour ? `Edit ${colour.brand} ${colour.name}` : 'Add colour'}
      intro={
        colour
          ? 'Every job card, item line and picker reads this colour from here.'
          : 'It goes on the list and into every colour picker the moment it is saved.'
      }
      width={560}
      onClose={close}
      actions={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={close}
            style={{ height: 48 }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready}
            aria-disabled={!ready}
            onClick={() => void st.saveColour()}
            style={{ height: 48, opacity: ready ? 1 : 0.55 }}
          >
            {st.savingColour
              ? 'Saving…'
              : colour
                ? 'Save changes'
                : clash?.archived
                  ? 'Bring it back'
                  : 'Add colour'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 2 }}>
        {st.colourError ? <DialogError>{st.colourError}</DialogError> : null}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px 12px',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span className="label-xs" style={label}>
              Brand
            </span>
            <input
              className="input"
              name="colourBrand"
              value={d.brand}
              list="colour-brands"
              maxLength={COLOUR_BRAND_MAX}
              placeholder="Dulux"
              autoComplete={NO_AUTOFILL}
              autoFocus={!colour}
              onChange={(e) => st.setColourDraft({ brand: e.target.value })}
              style={{ height: 48, fontSize: 16 }}
            />
            {/* The brands already on the list, offered as it is typed — so
                "Dulux" is one brand and not three spellings of it. */}
            <datalist id="colour-brands">
              {brands.map((b) => (
                <option key={b} value={b} />
              ))}
            </datalist>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span className="label-xs" style={label}>
              Code
            </span>
            <input
              className="input"
              name="colourCode"
              value={d.code}
              maxLength={COLOUR_CODE_MAX}
              placeholder="PN1G7"
              autoComplete={NO_AUTOFILL}
              spellCheck={false}
              autoCapitalize="characters"
              onChange={(e) => st.setColourDraft({ code: e.target.value })}
              style={{ height: 48, fontSize: 16, fontFamily: 'var(--font-data)' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5, gridColumn: '1 / -1' }}>
            <span className="label-xs" style={label}>
              Name
            </span>
            <input
              className="input"
              name="colourName"
              value={d.name}
              maxLength={COLOUR_NAME_MAX}
              placeholder="Vivid White"
              autoComplete={NO_AUTOFILL}
              onChange={(e) => st.setColourDraft({ name: e.target.value })}
              style={{ height: 48, fontSize: 16 }}
            />
          </label>
        </div>

        {clash ? (
          <p
            role={refused ? 'alert' : 'status'}
            style={{
              margin: 0,
              padding: '10px 12px',
              borderRadius: 10,
              background: refused ? 'var(--urgent-tint)' : 'var(--accent-tint)',
              border: `1px solid ${refused ? 'var(--urgent)' : 'var(--accent)'}`,
              fontSize: 13.5,
              lineHeight: 1.45,
            }}
          >
            {refused
              ? clash.archived
                ? `${clash.code} is ${clash.brand} ${clash.name}, which is archived. Bring that one back from the list instead.`
                : `${clash.code} is already on the list — ${clash.brand} ${clash.name}. One code is one colour.`
              : `${clash.code} was ${clash.brand} ${clash.name}, and it is archived. Adding it brings that colour back, with what you type here.`}
          </p>
        ) : recoded ? (
          <p style={hint}>
            {colour!.jobs} {colour!.jobs === 1 ? 'job is' : 'jobs are'} in {colour!.code}. They move
            to the new code with it — this corrects the colour, it does not recolour the jobs.
          </p>
        ) : null}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="label-xs" style={label}>
            Swatch
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <input
              type="color"
              name="colourSwatch"
              aria-label="Pick the swatch"
              value={hexOk ? d.hex.trim().toLowerCase() : '#ffffff'}
              autoComplete={NO_AUTOFILL}
              onChange={(e) => st.setColourDraft({ hex: e.target.value.toUpperCase() })}
              style={{
                width: 64,
                height: 48,
                padding: 3,
                flex: 'none',
                borderRadius: 10,
                border: '1px solid var(--line)',
                background: 'var(--surface)',
                cursor: 'pointer',
              }}
            />
            <input
              className="input"
              name="colourHex"
              aria-label="Swatch hex"
              value={d.hex}
              maxLength={7}
              placeholder="#F7F6F1"
              autoComplete={NO_AUTOFILL}
              spellCheck={false}
              onChange={(e) => st.setColourDraft({ hex: e.target.value })}
              style={{
                width: 132,
                height: 48,
                fontSize: 16,
                fontFamily: 'var(--font-data)',
              }}
            />
            {/* The chip as the board will draw it: the swatch, and the code in
                mono beneath it. Drawn from the draft rather than the list, so
                it moves as the swatch does. */}
            <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <span
                aria-hidden="true"
                style={{
                  display: 'block',
                  width: 40,
                  height: 40,
                  flex: 'none',
                  borderRadius: 10,
                  background: hexOk ? d.hex.trim() : 'transparent',
                  border: hexOk ? '1px solid var(--chip-border)' : '1px dashed var(--line)',
                  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.5)',
                }}
              />
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, overflowWrap: 'anywhere' }}>
                  {d.name.trim() || 'Name'}
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-data)',
                    fontSize: 12,
                    color: 'var(--ink-muted)',
                  }}
                >
                  {d.code.trim() || 'CODE'}
                </span>
              </span>
            </span>
          </div>
          <p style={hint}>
            {hexOk
              ? 'Approximate — it is for reading the board, never for matching a paint. The code is what gets re-ordered.'
              : 'The swatch is a colour like #F7F6F1. Pick it, or type it off the fan deck.'}
          </p>
        </div>
      </div>
    </Dialog>
  );
}

/** What went wrong, in the server's words, at the top of a dialog. */
/** The notice form: what the shop is being told, and how loudly.
 *
 *  A draft like the customer and person forms, so Cancel means the board is as
 *  it was — the whole shop reads this, and half a retyped sentence in front of
 *  them is worse than the sentence that was there.
 *
 *  Only the title is required. Most notices are one line, and a form that
 *  insisted on a paragraph would get one word typed twice. */
function NoticeDialog() {
  const st = useApp();
  const close = () => st.leave(() => st.set('noticeOpen', false));
  const d = st.noticeDraft;
  const editing = st.noticeEditId;
  const ready = d.title.trim().length > 0 && !st.savingNotice;

  return (
    <Dialog
      title={editing ? 'Edit notice' : 'Put a notice up'}
      intro="Everyone in the shop sees this on their dashboard, subcontractors included."
      width={520}
      onClose={close}
      actions={
        <>
          {editing ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                void st.takeNoticeDown(editing);
              }}
              style={{ height: 48, marginRight: 'auto', color: 'var(--urgent)' }}
            >
              Take down
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={close}
            style={{ height: 48 }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready}
            aria-disabled={!ready}
            onClick={() => {
              void st.saveNotice();
            }}
            style={{ height: 48, opacity: ready ? 1 : 0.55 }}
          >
            {editing ? 'Save changes' : 'Put it up'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 2 }}>
        {st.noticeError ? <DialogError>{st.noticeError}</DialogError> : null}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
            Notice
          </span>
          <input
            className="input"
            name="noticeTitle"
            value={d.title}
            maxLength={NOTICE_TITLE_MAX}
            placeholder="No deliveries Friday — the truck is booked"
            autoComplete={NO_AUTOFILL}
            autoFocus
            onChange={(e) => st.setNoticeDraft({ title: e.target.value })}
            style={{ height: 48, fontSize: 16 }}
          />
          <span style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
            The line the shop reads. Keep it to one.
          </span>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
            Detail
          </span>
          <textarea
            className="input"
            name="noticeBody"
            value={d.body}
            autoComplete={NO_AUTOFILL}
            rows={4}
            maxLength={NOTICE_BODY_MAX}
            placeholder="Anything else the shop needs. Leave it blank if the line above says it."
            onChange={(e) => st.setNoticeDraft({ body: e.target.value })}
            style={{ fontSize: 16, minHeight: 96, padding: 12, resize: 'vertical' }}
          />
        </label>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
            How it reads
          </span>
          <Seg
            fill
            height={48}
            options={NOTICE_LEVELS.map((level) => ({
              label: NOTICE_LEVEL_LABEL[level],
              on: d.level === level,
              pick: () => st.setNoticeDraft({ level }),
            }))}
          />
          <span style={{ fontSize: 12, color: 'var(--ink-muted)', lineHeight: 1.45 }}>
            {d.level === 'urgent'
              ? 'Drawn in the urgent colour with the word beside it. Keep it for what the shop has to act on today.'
              : 'The ordinary thing pinned on the board.'}
          </span>
        </div>
      </div>
    </Dialog>
  );
}

function DialogError({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      style={{
        margin: 0,
        padding: '10px 12px',
        borderRadius: 10,
        background: 'var(--urgent-tint)',
        border: '1px solid var(--urgent)',
        color: 'var(--ink)',
        fontSize: 13.5,
        lineHeight: 1.45,
      }}
    >
      {children}
    </p>
  );
}

/** One picker for every reference-data list: component type, substrate, edge,
 *  finish, colour, and the stage tint. */
function PickerDialog() {
  const st = useApp();
  const p = st.picker;
  if (!p) return null;
  const bulk = p.rowId === null;

  if (p.kind === 'stageTone' && p.stageId) {
    const stageId = p.stageId;
    return (
      <PickerShell
        title={`Board colour — ${stageName(st, stageId)}`}
        hint="Tints this stage’s column on the board. The stage name is always shown as well."
      >
        {TONES.map((t) => (
          <button
            key={t.name}
            type="button"
            onClick={() => st.setStageTone(stageId, t.hex)}
            style={optionRow(toneOf(st, stageId) === t.hex)}
          >
            <span
              style={{
                display: 'block',
                width: 22,
                height: 22,
                flex: 'none',
                borderRadius: 6,
                background: t.hex || 'transparent',
                border: t.hex ? '1px solid var(--chip-border)' : '1px dashed var(--line)',
              }}
            />
            <span style={{ fontSize: 14 }}>{t.name}</span>
            <span
              style={{
                fontFamily: 'var(--font-data)',
                fontSize: 11.5,
                color: 'var(--ink-muted)',
                marginLeft: 'auto',
              }}
            >
              {t.hex || '—'}
            </span>
          </button>
        ))}
      </PickerShell>
    );
  }

  if (p.kind === 'jobColour') {
    const job = currentJob(st);
    if (!job) return null;
    return (
      <PickerShell
        title={`Colour for ${job.code}`}
        hint="Changes the colour on the job and every item on it. The code is what gets re-ordered."
      >
        {liveColours(st).map((c) => (
          <ColourOption
            key={c.code}
            code={c.code}
            label={`${c.brand} ${c.name}`}
            selected={c.code === job.colour}
            onPick={() => st.setJobColour(c.code)}
          />
        ))}
      </PickerShell>
    );
  }

  if (p.kind === 'colour') {
    return (
      <PickerShell
        title={bulk ? 'Set colour on selected rows' : 'Colour'}
        hint="Brand, code and name. The code is what gets re-ordered."
      >
        {liveColours(st).map((c) => (
          <ColourOption
            key={c.code}
            code={c.code}
            label={`${c.brand} ${c.name}`}
            selected={false}
            onPick={() => st.applyToRows({ colour: c.code })}
          />
        ))}
      </PickerShell>
    );
  }

  const def = PICKERS[p.kind];
  return (
    <PickerShell
      title={bulk ? `Set ${def.title.toLowerCase()} on selected rows` : def.title}
      hint={def.hint}
    >
      {def.opts.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() =>
            st.applyToRows(
              // Coat count pre-fills from the finish: gloss wants three.
              p.kind === 'finish'
                ? ({ finish: o, coats: o === 'Gloss' ? 3 : 2 } as Partial<Item>)
                : ({ [p.kind]: o } as Partial<Item>),
            )
          }
          style={optionRow(false)}
        >
          <span style={{ fontSize: 14 }}>{o}</span>
        </button>
      ))}
    </PickerShell>
  );
}

function ColourOption({
  code,
  label,
  selected,
  onPick,
}: {
  code: string;
  label: string;
  selected: boolean;
  onPick: () => void;
}) {
  return (
    <button type="button" onClick={onPick} style={optionRow(selected)}>
      <Swatch code={code} size={22} />
      <span style={{ fontSize: 14 }}>{label}</span>
      <span
        style={{
          fontFamily: 'var(--font-data)',
          fontSize: 11.5,
          color: 'var(--ink-muted)',
          marginLeft: 'auto',
        }}
      >
        {code}
      </span>
    </button>
  );
}

function PickerShell({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  const closePicker = useApp((s) => s.closePicker);
  return (
    <Dialog
      title={title}
      intro={hint}
      width={460}
      onClose={closePicker}
      actions={
        <button
          type="button"
          className="btn btn-secondary"
          onClick={closePicker}
          style={{ height: 48 }}
        >
          Cancel
        </button>
      }
    >
      <input
        className="input"
        placeholder="Search"
        aria-label="Search"
        autoComplete={NO_AUTOFILL}
        style={{ height: 48, fontSize: 16 }}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          maxHeight: '48vh',
          overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </Dialog>
  );
}
